import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { BackButton } from '../components/BackButton';

interface CreateProfilePageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const CreateProfilePage: React.FC<CreateProfilePageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    filiere: 'Économie d\'entreprise',
    role: 'etudiant' as 'etudiant' | 'repetiteur' | 'les_deux',
    tarif: 0,
    annee: '1ère année',
    langue: 'francais' as 'francais' | 'anglais',
    email_contact: '',
    telephone_contact: '',
    matieres: [] as string[]
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filieres = [
    'Économie d\'entreprise',
    'Informatique de gestion',
    'Information documentaire',
    'International Business Management'
  ];

  const annees = [
    'Année académique : 1',
    'Année académique : 2', 
    'Année académique : 3'
  ];

  const subjectsByFiliere: Record<string, string[]> = {
    'Économie d\'entreprise': [
      'Comptabilité', 'Finance', 'Marketing', 'Management', 'Économie', 'Droit des affaires',
      'Statistiques', 'Mathématiques financières', 'Gestion de projet', 'Ressources humaines', 'Mathématiques'
    ],
    'Informatique de gestion': [
      'Programmation', 'Base de données', 'Réseaux', 'Sécurité informatique', 'Gestion de projet IT',
      'Analyse de systèmes', 'Développement web', 'Intelligence artificielle', 'Big Data', 'Mathématiques'
    ],
    'Information documentaire': [
      'Bibliothéconomie', 'Archivistique', 'Gestion de l\'information', 'Recherche documentaire',
      'Métadonnées', 'Numérisation', 'Veille informationnelle', 'Droit de l\'information', 'Mathématiques'
    ],
    'International Business Management': [
      'International Marketing', 'Global Economics', 'Cross-cultural Management', 'International Law',
      'Supply Chain Management', 'International Finance', 'Business English', 'Negotiation', 'Mathématiques'
    ]
  };

  useEffect(() => {
    const checkAndSetup = async () => {
      if (!authLoading && !user) {
        showToast('Vous devez être connecté pour créer un profil', 'error');
        navigate('/login');
        return;
      }
      
      if (user) {
        // Pré-remplir avec l'email de l'utilisateur
        setFormData(prev => ({
          ...prev,
          email_contact: user.email || '',
          prenom: user.user_metadata?.first_name || '',
          nom: user.user_metadata?.last_name || ''
        }));
        
        // Vérifier si l'utilisateur a déjà un profil
        try {
          const { data: profileData } = await api.profiles.getByUserId(user.id);
          
          if (profileData) {
            showToast('Vous avez déjà un profil. Redirection vers votre profil.', 'success');
            navigate('/profile');
            return;
          }
        } catch (err) {
        }
      }
    };
    
    checkAndSetup();
  }, [user, authLoading, navigate, showToast]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'tarif' ? Number(value) : value
      };
      
      // Si on change le rôle vers étudiant, mettre le tarif à 0
      if (name === 'role' && value === 'etudiant') {
        newData.tarif = 0;
      }
      // Si on change vers tuteur, mettre un tarif par défaut dans la fourchette
      if (name === 'role' && (value === 'repetiteur' || value === 'les_deux') && newData.tarif === 0) {
        newData.tarif = 30; // Tarif par défaut au milieu de la fourchette 25-60
      }
      
      return newData;
    });
  };

  const handleSubjectToggle = (matiere: string) => {
    setFormData(prev => ({
      ...prev,
      matieres: prev.matieres.includes(matiere)
        ? prev.matieres.filter(m => m !== matiere)
        : [...prev.matieres, matiere]
    }));
  };

  const validateForm = () => {
    const required = ['prenom', 'nom', 'filiere', 'annee', 'email_contact'];
    
    for (const field of required) {
      if (!formData[field as keyof typeof formData]) {
        showToast(`Le champ ${field === 'email_contact' ? 'email de contact' : field} est obligatoire`, 'error');
        return false;
      }
    }

    // Validation spécifique pour les tuteurs
    if (formData.role === 'repetiteur' || formData.role === 'les_deux') {
      if (formData.matieres.length === 0) {
        showToast('Veuillez sélectionner au moins une matière que vous pouvez enseigner', 'error');
        return false;
      }
      if (formData.tarif < 25 || formData.tarif > 60) {
        showToast('Le tarif horaire doit être entre 25 et 60 CHF', 'error');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) {
      showToast('Erreur d\'authentification', 'error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const profileData = {
        user_id: user.id,
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        filiere: formData.filiere,
        matieres: formData.matieres.join(', '),
        tarif: formData.tarif,
        role: formData.role,
        annee: formData.annee,
        langue: formData.langue,
        email_contact: formData.email_contact.trim(),
        telephone_contact: formData.telephone_contact.trim()
      };

      const { data, error } = await api.profiles.create(profileData);

      if (error) {
        const errorMessage = (error as any)?.message || error.toString();
        if (errorMessage.includes('duplicate') || errorMessage.includes('unique')) {
          showToast('Vous avez déjà un profil. Redirection vers votre profil.', 'error');
          navigate('/profile');
        } else {
          showToast('Erreur lors de la création du profil. Veuillez réessayer.', 'error');
        }
        return;
      }

      if (data) {
        
        const roleNames = {
          'etudiant': 'étudiant-e',
          'repetiteur': 'tuteur-trice',
          'les_deux': 'étudiant-e et tuteur-trice'
        };

        showToast(`Profil ${roleNames[formData.role]} créé avec succès ! Bienvenue dans HEG-Genève Tutor.`, 'success');
        navigate('/profile');
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <BackButton />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Créer votre profil</h1>
          <p className="text-gray-600">Complétez vos informations pour rejoindre la communauté HEG-Genève Tutor</p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            
            {/* Informations personnelles */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Informations personnelles
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filière <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="filiere"
                    value={formData.filiere}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                    required
                  >
                    <option value="">Choisissez votre filière</option>
                    {filieres.map(filiere => (
                      <option key={filiere} value={filiere}>{filiere}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Année d'études <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="annee"
                    value={formData.annee}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                    required
                  >
                    <option value="">Choisissez votre année</option>
                    {annees.map(annee => (
                      <option key={annee} value={annee}>{annee}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Rôle */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Votre rôle
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="relative">
                  <input
                    type="radio"
                    name="role"
                    value="etudiant"
                    checked={formData.role === 'etudiant'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.role === 'etudiant' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <div className="text-center">
                      <i className="fas fa-graduation-cap text-2xl text-blue-600 mb-2"></i>
                      <h4 className="font-semibold">Étudiant-e</h4>
                      <p className="text-sm text-gray-600">Je cherche de l'aide</p>
                    </div>
                  </div>
                </label>

                <label className="relative">
                  <input
                    type="radio"
                    name="role"
                    value="repetiteur"
                    checked={formData.role === 'repetiteur'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.role === 'repetiteur' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <div className="text-center">
                      <i className="fas fa-chalkboard-teacher text-2xl text-green-600 mb-2"></i>
                      <h4 className="font-semibold">Tuteur-trice</h4>
                      <p className="text-sm text-gray-600">J'aide les autres</p>
                    </div>
                  </div>
                </label>

                <label className="relative">
                  <input
                    type="radio"
                    name="role"
                    value="les_deux"
                    checked={formData.role === 'les_deux'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.role === 'les_deux' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <div className="text-center">
                      <i className="fas fa-exchange-alt text-2xl text-purple-600 mb-2"></i>
                      <h4 className="font-semibold">Les deux</h4>
                      <p className="text-sm text-gray-600">J'aide et je cherche de l'aide</p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Matières (seulement pour tuteurs) */}
            {(formData.role === 'repetiteur' || formData.role === 'les_deux') && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Matières enseignées <span className="text-red-500">*</span>
                </h3>
                
                {formData.filiere && subjectsByFiliere[formData.filiere] && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {subjectsByFiliere[formData.filiere].map(matiere => (
                      <label key={matiere} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.matieres.includes(matiere)}
                          onChange={() => handleSubjectToggle(matiere)}
                          className="rounded border-gray-300 text-[#002c46] focus:ring-[#002c46]"
                        />
                        <span className="text-sm">{matiere}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tarif (seulement pour tuteurs) */}
            {(formData.role === 'repetiteur' || formData.role === 'les_deux') && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Tarif horaire <span className="text-red-500">*</span>
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarif en CHF par heure
                  </label>
                  <input
                    type="number"
                    name="tarif"
                    value={formData.tarif}
                    onChange={handleChange}
                    min="25"
                    max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                    placeholder="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Entre 25 et 60 CHF/h
                  </p>
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Informations de contact
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de contact <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email_contact"
                  value={formData.email_contact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  name="telephone_contact"
                  value={formData.telephone_contact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#002c46] focus:border-[#002c46]"
                  placeholder="+41 XX XXX XX XX"
                />
              </div>
            </div>

            {/* Bouton de soumission */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#002c46] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Création du profil...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check mr-2"></i>
                    Créer mon profil
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Aide */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600 mb-2">
            Besoin d'aide ?
          </p>
          <a
            href="/contact"
            className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
          >
            <i className="fas fa-envelope mr-1"></i>
            Contactez-nous
          </a>
        </div>
      </div>
    </div>
  );
};